<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Checkbox from 'primevue/checkbox'
import RadioButton from 'primevue/radiobutton'
import LockableInputWrapper from '@/components/LockableInputWrapper.vue'

export type RadioRowOption = {
  label: string
  disabled?: boolean
  locked?: boolean
}

const props = withDefaults(
  defineProps<{
    optionSet: RadioRowOption[]
    groupLabel: string
    defaultOptions?: string[]
    columns?: number
    labelPosition?: 'top' | 'right'
    multiSelect?: boolean
    idPrefix?: string
  }>(),
  {
    defaultOptions: () => [''],
    columns: 0,
    labelPosition: 'top',
    multiSelect: false,
    idPrefix: ''
  }
)

const emit = defineEmits<{ change: [selectedOptions: string[]] }>()

const isLocked = (label: string) => props.optionSet.some((option) => option.label === label && option.locked)

function initialSelectedOption(): string {
  const defaultOption = props.defaultOptions[0]
  if (!isLocked(defaultOption)) {
    return defaultOption
  }
  return props.optionSet.find((option) => !option.locked)?.label ?? ''
}

const selectedOption = ref(initialSelectedOption())
const selectedOptions = ref<string[]>([...props.defaultOptions])

watch(selectedOption, (option) => {
  selectedOptions.value = [option]
})

watch(selectedOptions, (options) => emit('change', options))

watch(
  () => props.multiSelect,
  (isMultiSelect) => {
    if (isMultiSelect) {
      selectedOptions.value = [selectedOption.value]
      return
    }
    selectedOption.value = selectedOptions.value[0] ?? initialSelectedOption()
    emit('change', [selectedOption.value])
  }
)

const columnStyle = computed(() => (props.columns ? { flexBasis: `${100 / props.columns}%` } : {}))

// Radios need a shared name per row so screen readers count "N of M" within it,
// and several rows render on one page (one per skill).
const radioGroupName = computed(() => {
  const base = props.idPrefix || props.groupLabel
  return base.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'radio-row'
})

function inputId(option: RadioRowOption): string {
  return props.idPrefix ? `${props.idPrefix}-${option.label.trim().split(' ').join('-')}` : option.label
}

const labelClass = (option: RadioRowOption) => ({ 'text-gray-700': option.disabled })
</script>

<template>
  <div class="flex w-full flex-row flex-wrap items-start" role="group" :aria-label="groupLabel">
    <div
      v-for="option in optionSet"
      :key="option.label"
      class="shrink grow-0 basis-0"
      :class="{ 'm-0 text-center': labelPosition === 'top' }"
      :style="columnStyle"
    >
      <label v-if="labelPosition === 'top'" :for="inputId(option)" class="mb-0 block" :class="labelClass(option)">
        {{ option.label }}
      </label>
      <div class="inline-flex items-center" :class="{ 'cursor-not-allowed': option.locked }">
        <LockableInputWrapper :locked="option.locked" class="p-2">
          <Checkbox
            v-if="multiSelect"
            v-model="selectedOptions"
            :value="option.label"
            :input-id="inputId(option)"
            :disabled="option.disabled || option.locked"
          />
          <RadioButton
            v-else
            v-model="selectedOption"
            :value="option.label"
            :input-id="inputId(option)"
            :name="radioGroupName"
            :disabled="option.disabled || option.locked"
          />
        </LockableInputWrapper>
        <label v-if="labelPosition === 'right'" :for="inputId(option)" class="mb-0" :class="labelClass(option)">
          {{ option.label }}
        </label>
      </div>
    </div>
  </div>
</template>
